import { useState, useEffect, useCallback } from 'react';
import FormattedMessage from 'I18n/components/FormattedMessage';
import ErrorBoundary from 'customer-data-objects-ui-components/ErrorBoundary';
import UIDropdown from 'UIComponents/dropdown/UIDropdown';
import UIFlex from 'UIComponents/layout/UIFlex';
import UIList from 'UIComponents/list/UIList';
import SendEmailModal from './emails/SendEmailModal';
import SendEmailActionButton from './emails/SendEmailActionButton';
import getPropertyValue from '../../dataAccessHelpers/getPropertyValue';
import RefundModal from './refund/RefundModal';
import RefundSyncedPaymentConfirmationModal from './refund/RefundSyncedPaymentConfirmationModal';
import RefundActionButton from './refund/RefundActionButton';
import DeleteActionButton from './delete/DeleteActionButton';
import DeleteModal from './delete/DeleteModal';
import SendAuthorizationCopyActionButton from './authorization/SendAuthorizationCopyActionButton';
import SendAuthorizationCopyModal from './authorization/SendAuthorizationCopyModal';
import { usageTracker } from '../../utils/usageTracker';
import useFetchPotentiallyStaleProperties from '../../hooks/useFetchPotentiallyStaleProperties';
import { REFRESH_PAYMENT_TIMELINE_EVENTS } from '../../utils/EventConstants';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
function getCanSendAuthorizationCopy(paymentMethodType = '') {
  const allowedPaymentMethods = new Set(['ach', 'bacs', 'pads', 'sepa']);
  return allowedPaymentMethods.has(paymentMethodType);
}
const PaymentSidebarActions = ({
  objectId,
  paymentHighlightDataCrmObject
}) => {
  const [refundTriggered, setRefundTriggered] = useState(false);
  const [sendEmailModalOpen, setSendEmailModalOpen] = useState(false);
  const [refundModalOpen, setRefundModalOpen] = useState(false);
  const [refundSyncedPaymentConfirmationModalOpen, setRefundSyncedPaymentConfirmationModalOpen] = useState(false);
  const [sendAuthorizationCopyModalOpen, setSendAuthorizationCopyModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const fetchUpdatedProperties = useFetchPotentiallyStaleProperties(objectId);
  const properties = paymentHighlightDataCrmObject.defaultProperties;
  const associations = paymentHighlightDataCrmObject.defaultAssociations;
  const externalAccountId = paymentHighlightDataCrmObject.externalAccountId;
  const isAccountEnabled = paymentHighlightDataCrmObject.isAccountEnabled;
  const paymentIntentId = paymentHighlightDataCrmObject.paymentIntentId;
  const paymentStatus = getPropertyValue(properties, 'hs_latest_status');
  const processorType = getPropertyValue(properties, 'hs_processor_type');
  const paymentSource = getPropertyValue(properties, 'hs_object_source_label');
  const isManualPayment = processorType === 'none';
  const isSyncedPayment = processorType === 'quickbooks' || processorType === 'xero';
  const canSendAuthorizationCopy = !isManualPayment && !isSyncedPayment && getCanSendAuthorizationCopy(getPropertyValue(properties, 'hs_payment_method_type'));

  /**
   * This useEffect is doing two things:
   * 1. Prevent a race with the server updating the status after initiating a refund by disabling the button.
   *      Most CC based refunds will complete in less than 10 seconds, and the eligibility
   *      will be rechecked when the button rerenders (if the user tries to refund again).
   * 2. Refetch Payment properties that may have changed since the refund was initiated.
   *      This will also sync that data back to the host app if applicable.
   */
  useEffect(() => {
    const timeout = setTimeout(() => {
      fetchUpdatedProperties();
      setRefundTriggered(false);
    }, 1000 * 10);
    return () => clearTimeout(timeout);
  }, [fetchUpdatedProperties]);
  const handleRefundActionClicked = useCallback(() => {
    usageTracker.track('paymentCRMSidebarInteraction', {
      action: 'refundActionClicked'
    });
    if (isSyncedPayment) {
      setRefundSyncedPaymentConfirmationModalOpen(true);
    } else {
      setRefundModalOpen(true);
    }
  }, [isSyncedPayment]);
  const openSendAuthorizationCopyModal = useCallback(() => {
    usageTracker.track('paymentCRMSidebarInteraction', {
      action: 'sendAuthorizationCopyActionClicked'
    });
    setSendAuthorizationCopyModalOpen(true);
  }, []);
  const openDeleteModal = useCallback(() => {
    usageTracker.track('paymentCRMSidebarInteraction', {
      action: 'deleteActionClicked'
    });
    setDeleteModalOpen(true);
  }, []);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [sendEmailModalOpen && /*#__PURE__*/_jsx(ErrorBoundary, {
      boundaryName: "SendEmailModal",
      showRefreshAlert: true,
      children: /*#__PURE__*/_jsx(SendEmailModal, {
        customerEmail: getPropertyValue(properties, 'hs_customer_email'),
        onClose: () => {
          setSendEmailModalOpen(false);
        },
        objectId: parseInt(objectId, 10)
      })
    }), refundSyncedPaymentConfirmationModalOpen && /*#__PURE__*/_jsx(ErrorBoundary, {
      boundaryName: "RefundSyncedPaymentConfirmationModal",
      showRefreshAlert: true,
      children: /*#__PURE__*/_jsx(RefundSyncedPaymentConfirmationModal, {
        paymentSource: paymentSource,
        processorType: processorType,
        onConfirm: () => {
          setRefundSyncedPaymentConfirmationModalOpen(false);
          setRefundModalOpen(true);
        },
        onCancel: () => setRefundSyncedPaymentConfirmationModalOpen(false)
      })
    }), refundModalOpen && /*#__PURE__*/_jsx(ErrorBoundary, {
      boundaryName: "RefundModal",
      showRefreshAlert: true,
      children: /*#__PURE__*/_jsx(RefundModal, {
        objectId: objectId,
        paymentSource: paymentSource,
        onClose: () => setRefundModalOpen(false),
        onRefundSuccess: () => {
          window.dispatchEvent(new CustomEvent(REFRESH_PAYMENT_TIMELINE_EVENTS));
          setRefundTriggered(true);
        }
      })
    }), sendAuthorizationCopyModalOpen && /*#__PURE__*/_jsx(ErrorBoundary, {
      boundaryName: "SendAuthorizationCopyModal",
      showRefreshAlert: true,
      children: /*#__PURE__*/_jsx(SendAuthorizationCopyModal, {
        properties: properties,
        onClose: () => setSendAuthorizationCopyModalOpen(false)
      })
    }), deleteModalOpen && /*#__PURE__*/_jsx(ErrorBoundary, {
      boundaryName: "DeleteModal",
      showRefreshAlert: true,
      children: /*#__PURE__*/_jsx(DeleteModal, {
        objectId: objectId,
        onClose: () => setDeleteModalOpen(false),
        properties: properties,
        associations: associations,
        isSyncedPayment: isSyncedPayment
      })
    }), /*#__PURE__*/_jsx(UIFlex, {
      justify: "end",
      children: /*#__PURE__*/_jsx(UIDropdown, {
        buttonUse: "transparent",
        buttonText: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "cardPaymentHighlight.paymentHighlight.sidebarActions.actions"
        }),
        "data-test-id": "payment-sidebar-actions",
        children: /*#__PURE__*/_jsxs(UIList, {
          "data-test-id": "payment-sidebar-actions-list",
          children: [!isManualPayment && /*#__PURE__*/_jsx(RefundActionButton, {
            objectId: objectId,
            externalAccountId: externalAccountId,
            isAccountEnabled: isAccountEnabled,
            paymentIntentId: paymentIntentId,
            processorType: processorType,
            refundTriggered: refundTriggered,
            onClick: handleRefundActionClicked
          }), !isSyncedPayment && /*#__PURE__*/_jsx(SendEmailActionButton, {
            isManualPayment: isManualPayment,
            isAccountEnabled: isAccountEnabled,
            disabled: paymentStatus === 'failed',
            openModal: () => {
              setSendEmailModalOpen(true);
            }
          }), canSendAuthorizationCopy && /*#__PURE__*/_jsx(SendAuthorizationCopyActionButton, {
            isAccountEnabled: isAccountEnabled,
            paymentStatus: paymentStatus,
            onClick: openSendAuthorizationCopyModal
          }), (isManualPayment || isSyncedPayment) && /*#__PURE__*/_jsx(DeleteActionButton, {
            openModal: openDeleteModal
          })]
        })
      })
    })]
  });
};
export default PaymentSidebarActions;